import { useEffect, useState } from 'react'
import { configVersionsApi, configVersionsAvailable, type ConfigEntityType, type ConfigVersionEntry } from '../api/configVersions'
import { Button, Callout, CircularProgress, DataTable, Dialog, EmptyState } from './ui'

/**
 * R4-03 (REQ-220-001): pulsante «Storico versioni» e finestra con le versioni di una configurazione (gara, obiettivo, script,
 * pipeline, modello). Non rende nulla se lo storico non e' disponibile per l'utente (non Admin o flag spento).
 */
export function ConfigVersionHistoryButton({ entityType, entityId, label }: {
  entityType: ConfigEntityType
  entityId: number
  label: string
}) {
  const [available, setAvailable] = useState(false)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    let alive = true
    void configVersionsAvailable().then(ok => { if (alive) setAvailable(ok) })
    return () => { alive = false }
  }, [])
  if (!available) return null
  return (
    <>
      <Button variant="text" size="sm" icon="history" onClick={() => setOpen(true)}>Storico versioni</Button>
      {open && <HistoryDialog entityType={entityType} entityId={entityId} label={label} onClose={() => setOpen(false)} />}
    </>
  )
}

function dateIt(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
}

function HistoryDialog({ entityType, entityId, label, onClose }: {
  entityType: ConfigEntityType
  entityId: number
  label: string
  onClose: () => void
}) {
  const [items, setItems] = useState<ConfigVersionEntry[] | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    let alive = true
    configVersionsApi.list(entityType, entityId)
      .then(r => { if (alive) setItems(r) })
      .catch(e => { if (alive) setError(e instanceof Error && e.message ? e.message : 'Impossibile caricare lo storico.') })
    return () => { alive = false }
  }, [entityType, entityId])

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Storico versioni: ${label}`}
      size="lg"
      actions={<Button variant="text" type="button" dismiss onClick={onClose}>Chiudi</Button>}
    >
      {error && <Callout tone="error">{error}</Callout>}
      {!error && items === null && <CircularProgress size="sm" />}
      {items?.length === 0 && <EmptyState icon="history" title="Nessuna versione registrata" />}
      {items?.map(v => (
        <section key={v.id} className="mb-3">
          <h3 className="md-typescale-title-small">
            Versione {v.version} · in vigore dal {new Date(v.validFrom).toLocaleDateString('it-IT')} · registrata il {dateIt(v.createdAt)}
          </h3>
          <p className="md-typescale-body-small text-muted">Motivo: {v.reason}</p>
          {v.changes.length > 0 && (
            <DataTable aria-label={`Modifiche della versione ${v.version}`}>
              <thead><tr><th>Campo</th><th>Prima</th><th>Dopo</th></tr></thead>
              <tbody>
                {v.changes.map(c => (
                  <tr key={c.field}><td>{c.field}</td><td>{c.before ?? '—'}</td><td>{c.after ?? '—'}</td></tr>
                ))}
              </tbody>
            </DataTable>
          )}
        </section>
      ))}
    </Dialog>
  )
}
